import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { submittalsApi } from './api';

// --- Submittals Hooks ---

export function useDocumentTemplates(params?: { agency?: string }) {
    return useQuery({
        queryKey: ['documentTemplates', params],
        queryFn: () => submittalsApi.listTemplates(params),
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateDocumentTemplate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: any) => submittalsApi.createTemplate(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentTemplates'] });
        },
    });
}

export function useDeleteDocumentTemplate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => submittalsApi.deleteTemplate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentTemplates'] });
        },
    });
}

export function useSubmittalSlots(pursuitId: number) {
    return useQuery({
        queryKey: ['submittalSlots', pursuitId],
        queryFn: () => submittalsApi.listSlots(pursuitId),
        enabled: !!pursuitId,
        staleTime: 1000 * 30, // 30 seconds since they poll/change often
    });
}

export function useCreateSubmittalSlot() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ pursuitId, data }: { pursuitId: number; data: any }) =>
            submittalsApi.createSlot(pursuitId, data),
        onSuccess: (_, { pursuitId }) => {
            queryClient.invalidateQueries({ queryKey: ['submittalSlots', pursuitId] });
        },
    });
}

export function useUpdateSubmittalSlot() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => submittalsApi.updateSlot(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['submittalSlots'] });
        },
    });
}

export function useDeleteSubmittalSlot() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => submittalsApi.deleteSlot(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['submittalSlots'] });
        },
    });
}

export function useAutofillSubmittalSlot() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => submittalsApi.triggerAutofill(id),
        onSuccess: () => {
            // We'll invalidate slots since status likely moved to 'drafting'
            queryClient.invalidateQueries({ queryKey: ['submittalSlots'] });
        },
    });
}

// --- Final Submittals Hooks ---

export function useFinalSubmittals(pursuitId: number) {
    return useQuery({
        queryKey: ['finalSubmittals', pursuitId],
        queryFn: () => submittalsApi.listFinalSubmittals(pursuitId),
        enabled: !!pursuitId,
        staleTime: 1000 * 30, // 30 seconds
    });
}

export function useCreateFinalSubmittal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ pursuitId, data }: { pursuitId: number; data: any }) =>
            submittalsApi.createFinalSubmittal(pursuitId, data),
        onSuccess: (_, { pursuitId }) => {
            queryClient.invalidateQueries({ queryKey: ['finalSubmittals', pursuitId] });
        },
    });
}

export function useUpdateFinalSubmittal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => submittalsApi.updateFinalSubmittal(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['finalSubmittals'] });
        },
    });
}

export function useDeleteFinalSubmittal() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => submittalsApi.deleteFinalSubmittal(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['finalSubmittals'] });
        },
    });
}
